'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["relAttr"];
import PropTypes from 'prop-types';
import { Component } from 'react';
import { pick } from 'rich-text-lib/utils/iterables';
import InsertLinkPopoverBodyV2 from 'rich-text-lib/components/links/InsertLinkPopoverBodyV2';
import { TrackableLinkFields, LinkTypes, ExternalLinkTypes } from 'rich-text-lib/constants/link';
import { getRelAttrTrackingValues, getMeetingLinkSlug, getUrlParts } from 'rich-text-lib/utils/link';
import { INSERT_LINK } from '../constants/usageTracking';
import { trackInteraction } from '../utils/usageTracking';
import TinyMcePopover from '../components/TinyMcePopover';
import { jsx as _jsx } from "react/jsx-runtime";
export default class LinkInsertPopover extends Component {
  constructor(props) {
    super(props);
    this.handleInsert = () => {
      const {
        linkType,
        href
      } = this.state;
      const {
        fetchMeetingBySlug,
        fetchPageBySlug,
        fetchFileByUrl
      } = this.props;

      // build api cache if cacheable link inserted
      switch (linkType) {
        case LinkTypes.PAGE:
          {
            const {
              domain,
              slug
            } = getUrlParts(href);
            fetchPageBySlug(slug, domain).catch(error => console.error(error));
            break;
          }
        case LinkTypes.FILE:
          {
            fetchFileByUrl(href).catch(error => console.error(error));
            break;
          }
        case LinkTypes.MEETING:
          {
            const meetingSlug = getMeetingLinkSlug(href);
            fetchMeetingBySlug(meetingSlug).catch(error => console.error(error));
            break;
          }
        default:
          {
            break;
          }
      }
      this.setState({
        didInsert: true
      }, () => {
        this.props.onRemove();
      });
    };
    this.handleUpdate = linkInfo => {
      this.setState(linkInfo);
    };
    this.handleCloseComplete = () => {
      if (this.state.didInsert) {
        this.props.onInsert(this.state);
        this.trackLinkInsertion();
      }
      this.props.removeUnInsertedLinks();
    };
    this.handleRemove = () => {
      if (this._mounted) {
        this.props.onRemove();
      }
      this.props.removeUnInsertedLinks();
    };
    this.handleLinkTypeChange = linkType => {
      const href = linkType === LinkTypes.VIEW_AS_WEBPAGE ? '{{ view_as_page_url }}' : '';
      this.setState({
        linkType,
        externalLink: ExternalLinkTypes.includes(linkType),
        href,
        fileMeta: null,
        isValidLink: true
      });
    };
    this.state = Object.assign({}, this._getInitialState(), {
      linkText: props.selectedText
    });
  }
  componentDidMount() {
    this._mounted = true;
  }
  componentDidUpdate({
    open: prevOpen
  }) {
    const {
      open,
      selectedText
    } = this.props;
    if (open && !prevOpen) {
      this.setState(Object.assign({}, this._getInitialState(), {
        linkText: selectedText
      }));
    }
  }
  componentWillUnmount() {
    this._mounted = false;
  }
  _getInitialState() {
    return {
      href: '',
      externalLink: false,
      relAttr: [],
      linkText: '',
      fetchedPages: [],
      likelyUrlMatches: false,
      fileMeta: null,
      linkType: LinkTypes.URL,
      isValidLink: true
    };
  }
  trackLinkInsertion() {
    const _pick = pick(TrackableLinkFields, this.state),
      {
        relAttr
      } = _pick,
      trackingValues = _objectWithoutPropertiesLoose(_pick, _excluded);
    trackInteraction(this.props.editor, 'tinymceLink', INSERT_LINK, Object.assign({}, trackingValues, getRelAttrTrackingValues(relAttr)));
  }
  render() {
    const {
      children,
      editor,
      target,
      fetchPages,
      fetchMeetingsByUsers,
      fetchAnchors,
      mountedToText,
      onSelectFile,
      showRefAndTargetControls,
      useEmailSubscription,
      allowFileDownload,
      allowMeetingLink,
      emailSubscriptionLinkTypes,
      allowAnchorLinks,
      allowRawLinksOnly,
      canModifyLinkText,
      open,
      allowEmailWebVersionLink
    } = this.props;
    const {
      href,
      externalLink,
      relAttr,
      linkType,
      fileMeta,
      linkText,
      isValidLink
    } = this.state;
    const PopoverContent = InsertLinkPopoverBodyV2;
    return /*#__PURE__*/_jsx(TinyMcePopover, {
      open: open,
      target: target,
      editor: editor,
      arrowSize: mountedToText ? 'medium' : 'none',
      placement: mountedToText ? 'bottom' : 'bottom right',
      onRemove: this.handleRemove,
      closeOnOutsideClick: !mountedToText,
      onOpenChange: !mountedToText ? this.handleRemove : null,
      onCloseComplete: this.handleCloseComplete,
      animateOnToggle: false,
      renderContent: () => /*#__PURE__*/_jsx(PopoverContent, {
        dropdownClassName: "mce-ui-select",
        tooltipClassName: "mce-link-tooltip",
        href: href,
        isValidLink: isValidLink,
        externalLink: externalLink,
        relAttr: relAttr,
        linkText: linkText,
        linkType: linkType,
        currentFileMeta: fileMeta,
        fetchPages: fetchPages,
        fetchMeetingsByUsers: fetchMeetingsByUsers,
        fetchAnchors: fetchAnchors,
        onClose: this.handleRemove,
        onLinkTypeChange: this.handleLinkTypeChange,
        onUpdate: this.handleUpdate,
        onInsert: this.handleInsert,
        onSelectFile: onSelectFile,
        showRefAndTargetControls: showRefAndTargetControls,
        useEmailSubscription: useEmailSubscription,
        allowFileDownload: allowFileDownload,
        allowMeetingLink: allowMeetingLink,
        emailSubscriptionLinkTypes: emailSubscriptionLinkTypes,
        allowAnchorLinks: allowAnchorLinks,
        allowRawLinksOnly: allowRawLinksOnly,
        canModifyLinkText: canModifyLinkText,
        scopes: editor.settings.scopes,
        userId: editor.settings.userId,
        renderLinkPopoverAlert: editor.settings.renderLinkPopoverAlert,
        allowEmailWebVersionLink: allowEmailWebVersionLink
      }),
      children: children
    });
  }
}
LinkInsertPopover.propTypes = {
  children: PropTypes.object,
  open: PropTypes.bool.isRequired,
  editor: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  target: PropTypes.oneOfType([PropTypes.exact(HTMLAnchorElement), PropTypes.exact(HTMLSpanElement)]),
  selectedText: PropTypes.string,
  fetchPages: PropTypes.func.isRequired,
  fetchMeetingsByUsers: PropTypes.func.isRequired,
  fetchMeetingBySlug: PropTypes.func.isRequired,
  fetchPageBySlug: PropTypes.func.isRequired,
  fetchFileByUrl: PropTypes.func.isRequired,
  fetchAnchors: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
  mountedToText: PropTypes.bool.isRequired,
  onSelectFile: PropTypes.func.isRequired,
  showRefAndTargetControls: PropTypes.bool.isRequired,
  useEmailSubscription: PropTypes.bool,
  allowFileDownload: PropTypes.bool,
  allowMeetingLink: PropTypes.bool,
  emailSubscriptionLinkTypes: PropTypes.array,
  allowAnchorLinks: PropTypes.bool,
  canModifyLinkText: PropTypes.bool,
  allowRawLinksOnly: PropTypes.bool,
  removeUnInsertedLinks: PropTypes.func,
  allowEmailWebVersionLink: PropTypes.bool
};